import { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Search, Check, ChevronDown } from 'lucide-react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  AsYouType,
  type CountryCode,
} from 'libphonenumber-js';
import { useDropdownPortal } from '../../hooks/useDropdownPortal';
import { normalizeText as normalize } from '../../utils/textNormalization';
import {
  FORM_BORDER,
  FORM_BORDER_HOVER,
  FORM_BORDER_ERROR,
  FOCUS_RING,
  FOCUS_RING_ERROR,
  FORM_RADIUS,
  FORM_SHADOW,
  DROPDOWN_ANIMATION,
  DROPDOWN_BG,
  ITEM_HOVER,
  LABEL_TEXT,
  ERROR_TEXT,
} from './tokens';

interface PhoneInputProps {
  readonly label: string;
  readonly value: string; // E.164, ej: "+56912345678" (o "" si vacío)
  readonly onChange: (value: string) => void; // emite E.164 o ""
  readonly error?: string;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly id?: string;
}

const DEFAULT_COUNTRY: CountryCode = 'CL';

interface CountryItem {
  readonly code: CountryCode;
  readonly name: string;
  readonly dial: string;
}

// Lista de países (con nombre en español y código telefónico), calculada una vez.
const COUNTRIES: readonly CountryItem[] = (() => {
  const regionNames = new Intl.DisplayNames(['es'], { type: 'region' });
  return getCountries()
    .map((code) => ({
      code,
      name: regionNames.of(code) ?? code,
      dial: getCountryCallingCode(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
})();

// Deriva país + número nacional desde un valor E.164 almacenado. Si no se puede inferir
// el país (valor malformado), conserva el país actual y deja solo los dígitos.
const parseValue = (value: string, current: CountryCode): { country: CountryCode; national: string } => {
  if (!value) return { country: DEFAULT_COUNTRY, national: '' };
  const parsed = parsePhoneNumberFromString(value);
  if (parsed?.country) return { country: parsed.country, national: parsed.nationalNumber };
  return { country: current, national: value.replace(/[^\d]/g, '') };
};

// Bandera circular servida como SVG individual desde nuestro propio dominio (public/flags/).
// El navegador descarga solo las banderas que se muestran (unos pocos KB), sin CDN externo.
const CircleFlag: React.FC<{ readonly code: string }> = ({ code }) => (
  <img
    src={`/flags/${code.toLowerCase()}.svg`}
    alt=""
    aria-hidden="true"
    loading="lazy"
    className="w-5 h-5 rounded-full shrink-0"
    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
  />
);

export const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  id,
}) => {
  const initialParsed = parseValue(value, DEFAULT_COUNTRY);
  const [country, setCountry] = useState<CountryCode>(initialParsed.country);
  const [national, setNational] = useState(initialParsed.national);
  const [syncedValue, setSyncedValue] = useState(value);

  // Sincroniza desde un cambio externo de `value` (ej: carga del formulario), ignorando
  // los ecos del propio onChange (cuando `value` ya coincide con lo que `country`/`national`
  // producirían). Comparar contra el estado actual en vez de un ref mutado en este mismo bloque:
  // un ref escrito durante el render no es idempotente bajo el doble-invoke de StrictMode — la
  // segunda invocación ve el ref ya mutado por la primera y se salta el sync.
  if (value !== syncedValue) {
    setSyncedValue(value);
    const currentE164 = national ? `+${getCountryCallingCode(country)}${national}` : '';
    if (value !== currentE164) {
      const next = parseValue(value, country);
      setCountry(next.country);
      setNational(next.national);
    }
  }

  const {
    isMounted,
    dropdownDataState,
    searchTerm,
    setSearchTerm,
    dropdownPos,
    containerRef,
    triggerRef,
    dropdownRef,
    handleOpen,
    handleAnimationEnd,
    close,
  } = useDropdownPortal<HTMLButtonElement>(disabled);

  const emit = (c: CountryCode, nat: string) => {
    const digits = nat.replace(/\D/g, '');
    const e164 = digits ? `+${getCountryCallingCode(c)}${digits}` : '';
    onChange(e164);
  };

  const handleCountrySelect = (c: CountryCode) => {
    setCountry(c);
    emit(c, national);
    close();
  };

  const handleNationalChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setNational(digits);
    emit(country, digits);
  };

  const filteredCountries = useMemo(() => {
    const q = normalize(searchTerm.trim());
    if (!q) return COUNTRIES;
    const qDigits = searchTerm.replace(/\D/g, '');
    return COUNTRIES.filter(
      (c) =>
        normalize(c.name).includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (qDigits.length > 0 && c.dial.startsWith(qDigits)),
    );
  }, [searchTerm]);

  const displayNational = national ? new AsYouType(country).input(national) : '';

  const rowClasses = [
    'flex items-stretch transition',
    FORM_RADIUS,
    error ? FORM_BORDER_ERROR : `${FORM_BORDER} ${FORM_BORDER_HOVER}`,
    disabled ? 'bg-slate-50 cursor-not-allowed' : 'bg-white',
  ].join(' ');

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor={id} className={`block ${LABEL_TEXT} mb-1.5`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className={rowClasses}>
        <button
          ref={triggerRef}
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          aria-label="Seleccionar código de país"
          className={`flex items-center gap-1.5 pl-3 pr-2 border-r border-slate-200 ${FORM_RADIUS} rounded-r-none ${error ? FOCUS_RING_ERROR : FOCUS_RING} ${disabled ? 'cursor-not-allowed' : ''}`}
        >
          <CircleFlag code={country} />
          <span className="text-sm text-slate-700 tabular-nums">+{getCountryCallingCode(country)}</span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>

        <input
          id={id}
          type="tel"
          inputMode="tel"
          value={displayNational}
          onChange={(e) => handleNationalChange(e.target.value)}
          disabled={disabled}
          placeholder="9 1234 5678"
          className={`flex-1 min-w-0 px-3 py-3 text-sm bg-transparent ${FORM_RADIUS} rounded-l-none outline-none ${error ? FOCUS_RING_ERROR : FOCUS_RING} disabled:cursor-not-allowed`}
        />
      </div>

      {isMounted &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            data-state={dropdownDataState}
            data-dropdown-portal="true"
            onAnimationEnd={handleAnimationEnd}
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: Math.max(dropdownPos.width, 288) }}
            className={`fixed z-[200] ${DROPDOWN_BG} ${FORM_RADIUS} ${FORM_SHADOW} ${DROPDOWN_ANIMATION} max-h-72 overflow-hidden dropdown-portal-protected`}
          >
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="País o código (ej: Chile o 56)"
                  aria-label="Buscar país"
                  className={`w-full pl-9 pr-3 py-2 text-sm ${FORM_BORDER} ${FORM_RADIUS} ${FOCUS_RING}`}
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-56 overflow-y-auto">
              {filteredCountries.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500 text-center">No se encontraron países</div>
              ) : (
                filteredCountries.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleCountrySelect(c.code)}
                    className={`w-full px-4 py-2.5 text-left ${ITEM_HOVER} transition-colors flex items-center gap-3 ${c.code === country ? 'bg-blue-50' : ''}`}
                  >
                    <CircleFlag code={c.code} />
                    <span className="flex-1 text-sm text-slate-800 truncate">{c.name}</span>
                    <span className="text-xs text-slate-500 tabular-nums">+{c.dial}</span>
                    {c.code === country && <Check className="w-4 h-4 text-blue-600 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}

      {error && <p className={ERROR_TEXT}>{error}</p>}
    </div>
  );
};
